import React, { useState } from 'react';
import { getScoreColor, getScoreBg, getScoreLabel } from '../../services/healthScoreService';

const NAVY   = '#0C2D48';
const BORDER = '#e0e0e0';

// ── Score ring ────────────────────────────────────────────────────────────────
function ScoreRing({ score }) {
  const color  = getScoreColor(score);
  const radius = 30;
  const circ   = 2 * Math.PI * radius;
  const dash   = (score / 100) * circ;

  return (
    <svg width={80} height={80} style={{ flexShrink: 0 }}>
      <circle cx={40} cy={40} r={radius} fill="none" stroke="#f0f0f0" strokeWidth={7} />
      <circle
        cx={40} cy={40} r={radius} fill="none"
        stroke={color} strokeWidth={7}
        strokeDasharray={`${dash} ${circ - dash}`}
        strokeLinecap="round"
        transform="rotate(-90 40 40)"
      />
      <text x={40} y={44} textAnchor="middle" fontSize={16} fontWeight={800}
            fill={color} fontFamily="Raleway, sans-serif">
        {score}
      </text>
    </svg>
  );
}

// ── Factor bar ────────────────────────────────────────────────────────────────
function FactorRow({ factor }) {
  const pct   = factor.max > 0 ? (factor.points / factor.max) * 100 : 0;
  const color = pct >= 75 ? '#27ae60' : pct >= 40 ? '#f59e0b' : '#E96A6A';

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.3rem' }}>
        <span style={{ fontSize: '0.82rem', fontWeight: 600, color: NAVY }}>{factor.label}</span>
        <span style={{ fontSize: '0.78rem', fontWeight: 700, color }}>
          {factor.points} / {factor.max}
        </span>
      </div>
      <div style={{ height: 5, background: '#f0f0f0', borderRadius: 3, overflow: 'hidden', marginBottom: '0.3rem' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 3, transition: 'width 0.5s ease' }} />
      </div>
      <p style={{ margin: 0, fontSize: '0.78rem', color: '#777', lineHeight: 1.4, fontStyle: 'italic' }}>
        {factor.insight}
      </p>
    </div>
  );
}

// ── Log a contact inline form ─────────────────────────────────────────────────
function LogContactForm({ onLog, onCancel }) {
  const [type,    setType]    = useState('call');
  const [summary, setSummary] = useState('');

  const TYPES = [
    { value: 'email',   label: '✉️ Email' },
    { value: 'call',    label: '📞 Call' },
    { value: 'meeting', label: '🤝 Meeting' },
    { value: 'note',    label: '📝 Note' },
  ];

  function handleLog() {
    if (!summary.trim()) return;
    onLog({
      id:      Date.now(),
      type,
      date:    new Date().toISOString(),
      summary: summary.trim(),
    });
    setSummary('');
  }

  return (
    <div style={{
      border: `1px solid ${BORDER}`, borderRadius: 10,
      padding: '0.85rem', background: '#fafafa', marginTop: '0.25rem',
    }}>
      <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.6rem' }}>
        Log a contact
      </div>
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
        {TYPES.map(t => (
          <button
            key={t.value}
            onClick={() => setType(t.value)}
            style={{
              padding: '0.25rem 0.65rem',
              borderRadius: 8,
              border: type === t.value ? `1.5px solid ${NAVY}` : '1px solid #ddd',
              background: type === t.value ? NAVY : '#fff',
              color: type === t.value ? '#fff' : '#555',
              fontSize: '0.78rem',
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: "'Raleway', sans-serif",
            }}
          >
            {t.label}
          </button>
        ))}
      </div>
      <textarea
        value={summary}
        onChange={e => setSummary(e.target.value)}
        placeholder="What happened? (e.g. Discussed renewal proposal)"
        rows={2}
        style={{
          width: '100%', boxSizing: 'border-box',
          padding: '0.5rem 0.65rem',
          borderRadius: 6, border: '1px solid #ddd',
          fontSize: '0.88rem', resize: 'vertical',
          fontFamily: "'Raleway', sans-serif",
        }}
      />
      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
        <button
          onClick={handleLog}
          disabled={!summary.trim()}
          style={{
            padding: '0.4rem 1rem',
            background: summary.trim() ? NAVY : '#e0e0e0',
            color: summary.trim() ? '#fff' : '#aaa',
            border: 'none', borderRadius: 6,
            fontSize: '0.83rem', fontWeight: 700, cursor: summary.trim() ? 'pointer' : 'default',
            fontFamily: "'Raleway', sans-serif",
          }}
        >
          Log
        </button>
        <button
          onClick={onCancel}
          style={{
            padding: '0.4rem 0.85rem',
            background: 'transparent', border: '1px solid #ddd', borderRadius: 6,
            fontSize: '0.83rem', cursor: 'pointer',
            fontFamily: "'Raleway', sans-serif", color: '#888',
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ── Main panel ────────────────────────────────────────────────────────────────
/**
 * ClientHealthScore
 *
 * Displayed inside the contact detail panel for "Client"-tagged contacts.
 *
 * Props:
 *   health      — result from calculateHealthScore() or scores[contact.id]
 *   onLogContact(interaction) — called when user logs a new contact
 *   onRefresh() — called when user clicks Refresh
 */
export default function ClientHealthScore({ health, onLogContact, onRefresh }) {
  const [showLog, setShowLog] = useState(false);

  if (!health) return null;

  const { score, label, color, factors } = health;
  const bg = getScoreBg(score);

  function handleLog(interaction) {
    onLogContact(interaction);
    setShowLog(false);
  }

  return (
    <div style={{
      border: `1px solid ${BORDER}`,
      borderLeft: `4px solid ${color}`,
      borderRadius: 12,
      background: bg,
      padding: '1.1rem 1.25rem',
      fontFamily: "'Raleway', sans-serif",
    }}>
      {/* Header row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
        <div>
          <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.2rem' }}>
            Client health score
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <ScoreRing score={score} />
            <div>
              <div style={{ fontSize: '1.1rem', fontWeight: 800, color }}>{label}</div>
              <div style={{ fontSize: '0.75rem', color: '#aaa', marginTop: '0.1rem' }}>
                {score} / 100 points
              </div>
            </div>
          </div>
        </div>
        <button
          onClick={onRefresh}
          title="Recalculate"
          style={{
            background: 'none', border: `1px solid ${BORDER}`, borderRadius: 6,
            cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600, color: '#777',
            padding: '0.3rem 0.65rem', fontFamily: "'Raleway', sans-serif",
          }}
        >
          Refresh
        </button>
      </div>

      {/* Factor breakdown */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.9rem', marginBottom: '1rem' }}>
        {factors.map(f => <FactorRow key={f.label} factor={f} />)}
      </div>

      {/* Log a contact */}
      {showLog ? (
        <LogContactForm onLog={handleLog} onCancel={() => setShowLog(false)} />
      ) : (
        <button
          onClick={() => setShowLog(true)}
          style={{
            padding: '0.45rem 1rem',
            background: NAVY, color: '#fff',
            border: 'none', borderRadius: 7,
            fontSize: '0.83rem', fontWeight: 700,
            cursor: 'pointer', fontFamily: "'Raleway', sans-serif",
          }}
        >
          + Log a contact
        </button>
      )}
    </div>
  );
}
